import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  TRAIL,
  REGIONS,
  getPlace,
  getRegion,
  loadProgress,
  placeState,
  regionProgress,
  nextTrailStop,
  dueEchoes,
  trailIndexOf,
} from "../lib/scaleCountry";
import { playScaleRun, playTone } from "../lib/scaleCountryAudio";
import CountryMap from "../components/scaleCountry/CountryMap";

// Land on the next unstamped trail stop so there's always something to do;
// once the whole trail is stamped, just open the start of it again.
const defaultPlace = (progress) => {
  const id = nextTrailStop(progress) ||
    TRAIL.find((pid) => placeState(progress, pid) !== "stamped") ||
    TRAIL[0];
  return getPlace(id);
};

const STATUS_LABEL = {
  new: "unvisited",
  visited: "visited · no phrase yet",
  stamped: "stamped · phrase saved",
};

/* ============================================================
   /scale-country — the map / home screen. Picking a place plays
   its scale (or, in the foggy Harmonic Highlands, a single low
   note — those places don't give up their sound so easily) and
   surfaces it in the info panel below.
   ============================================================ */

const ScaleCountry = () => {
  const navigate = useNavigate();
  // Read once on mount — place pages are the ones that update progress, and
  // remounting this page (a fresh route match) picks up any changes they made.
  const [progress] = useState(loadProgress);
  const [selected, setSelected] = useState(() => defaultPlace(loadProgress()));

  const region = getRegion(selected.regionId);
  const state = placeState(progress, selected.id);
  const nextStop = nextTrailStop(progress);
  const nextStopPlace = nextStop ? getPlace(nextStop) : null;
  const due = dueEchoes(progress);

  const handleSelect = (place) => {
    setSelected(place);
    const placeRegion = getRegion(place.regionId);
    if (placeRegion?.fog) {
      playTone(38, { durMs: 500, gain: 0.08, type: "sine" });
    } else {
      playScaleRun(place.semis);
    }
  };

  return (
    <div className="min-h-screen bg-[#1B221A] px-4 py-10 text-[#E9EDDC] sm:px-8">
      <div className="mx-auto max-w-3xl">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#79855F]">
              Scale Country
            </p>
            <h1 className="mt-2 font-serif text-3xl sm:text-4xl">
              Where to today?
            </h1>
            <p className="mt-2 text-sm text-[#97A18B]">
              ● stamped &nbsp; ◐ visited &nbsp; ○ new — click a place to hear
              it
            </p>
          </div>
          <Link
            to="/sillyprojects"
            className="whitespace-nowrap font-mono text-xs text-[#79855F] hover:text-[#A6C46A]"
          >
            part of silly projects →
          </Link>
        </div>

        <div className="mt-6 flex flex-wrap gap-3">
          {nextStopPlace ? (
            <Link
              to={`/scale-country/${nextStopPlace.id}`}
              className="rounded-md border border-[#45523F] bg-[#222B20] px-3 py-1.5 font-mono text-xs text-[#97A18B] hover:border-[#A6C46A] hover:text-[#E9EDDC]"
            >
              ▸ TRAIL — continue at {nextStopPlace.name} · stop{" "}
              {trailIndexOf(nextStopPlace.id) + 1} of {TRAIL.length}
            </Link>
          ) : (
            <span className="rounded-md border border-[#45523F] bg-[#222B20] px-3 py-1.5 font-mono text-xs text-[#97A18B]">
              ▸ TRAIL — walked end to end ●
            </span>
          )}
          {due.length > 0 ? (
            <Link
              to="/scale-country/campfire"
              className="rounded-md border border-[#45523F] bg-[#222B20] px-3 py-1.5 font-mono text-xs text-[#97A18B] hover:border-[#E0A458] hover:text-[#E9EDDC]"
            >
              ▸ CAMPFIRE — <span className="text-[#E0A458]">{due.length}</span>{" "}
              echo{due.length === 1 ? "" : "es"} due
            </Link>
          ) : (
            <span className="rounded-md border border-[#45523F] bg-[#222B20] px-3 py-1.5 font-mono text-xs text-[#79855F]">
              ▸ CAMPFIRE — quiet tonight, nothing due
            </span>
          )}
        </div>

        <div className="mt-8">
          <CountryMap
            progress={progress}
            selectedId={selected.id}
            onSelect={handleSelect}
          />
        </div>

        <div className="mt-6 flex flex-col gap-4 rounded-lg border border-[#39432F] bg-[#222B20] p-5 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="font-serif text-2xl">{selected.name}</h2>
            <p className="mt-1 font-mono text-sm text-[#A6C46A]">
              {selected.formula}
            </p>
            <span className="mt-2 inline-block rounded-md border border-[#45523F] px-2 py-0.5 font-mono text-[10px] uppercase tracking-wide text-[#97A18B]">
              {selected.moodTag}
            </span>
            <p className="mt-2 font-mono text-xs uppercase tracking-wide text-[#79855F]">
              {STATUS_LABEL[state]} · {region?.name}
            </p>
          </div>
          <div>
            {region?.fog ? (
              <span className="inline-block rounded-md border border-[#45523F] px-4 py-2 font-mono text-xs uppercase tracking-wide text-[#79855F] opacity-70">
                STILL IN THE FOG
              </span>
            ) : (
              <button
                type="button"
                onClick={() => navigate(`/scale-country/${selected.id}`)}
                className="rounded-md border border-[#45523F] px-4 py-2 font-mono text-xs font-bold uppercase tracking-wide text-[#E9EDDC] hover:border-[#A6C46A] hover:bg-[#A6C46A] hover:text-[#1B221A]"
              >
                OPEN PAGE ▸
              </button>
            )}
          </div>
        </div>

        <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2">
          {REGIONS.filter((r) => !r.fog).map((r) => {
            const { stamped, total } = regionProgress(progress, r.id);
            return (
              <p
                key={r.id}
                className="font-mono text-xs uppercase tracking-wide text-[#79855F]"
              >
                {r.name} {stamped}/{total} stamped
              </p>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ScaleCountry;
